import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
  Textarea,
  toast,
} from '@mlm/ui';
import { AR_PERMS, TAX_CODES } from '@mlm/shared';
import {
  createCreditMemo,
  deleteCreditMemoDraft,
  getCreditMemo,
  listArInvoices,
  listBranches,
  listCustomers,
  listGlAccounts,
  postCreditMemo,
  unapplyArApplication,
  updateCreditMemo,
  voidArCreditRefund,
  voidCreditMemo,
  type ArInvoiceLineInput,
  type CreditMemoStatus,
} from '../../lib/api';
import { useHasPerm } from '../../lib/auth-store';
import { ProductPicker } from '../../components/product-picker';
import { ApplyCreditDialog, RefundDialog } from './ar-receipts';

/**
 * The credit memo editor (R2 Phase E), modelled on the billing editor.
 *
 * A memo is a draft until it is posted — editable up to that moment, frozen
 * after. Posting turns its total into the customer's credit and, when it was
 * raised against a billing, applies it to that billing as far as the billing
 * still owes. The posted view is about where that credit went: applied to
 * billings, refunded, or still open.
 */

// Display helpers are duplicated from the sibling editors on purpose:
// exporting non-components from a page module breaks Fast Refresh.
function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** Local-timezone YYYY-MM-DD for `<input type="date">`. */
function toDateInput(iso?: string | null): string {
  const d = iso ? new Date(iso) : new Date();
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA');
}

function sanitizeMoney(v: string): string {
  return v.replace(/,/g, '').trim();
}

/** Seed editable money with the stored VALUE — trailing zeros trimmed, never re-rounded. */
function trimMoney(v: string | null | undefined): string {
  if (v == null || v === '') return '';
  const s = String(v);
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

const ERROR_COPY: Record<string, string> = {
  ar_invoicing_disabled: 'General billing is switched off for this tenant.',
  credit_memo_not_draft: 'Only draft credit memos can be edited, posted or deleted.',
  credit_memo_draft: 'Drafts are deleted, not voided.',
  credit_memo_applied: 'Some of this credit is applied to billings — unapply it first.',
  credit_memo_refunded: 'Some of this credit was refunded — void the refund first.',
  already_void: 'This credit memo is already void.',
  reason_required: 'Say why the customer is being credited.',
  draft_has_no_lines: 'Add at least one line before posting.',
  invalid_line: 'A line needs either a product or a description.',
  invalid_qty: 'Quantities must be whole numbers above zero.',
  invalid_amount: 'The total must be greater than zero.',
  invalid_unit_price: 'A unit price cannot be negative.',
  income_account_invalid: 'Pick an active income account for that line.',
  branch_required: 'Choose the branch the goods go back to.',
  restock_needs_products: 'Restocking needs at least one product line.',
  invoice_customer_mismatch: 'That billing belongs to a different customer.',
  invoice_void: 'That billing is void.',
  invoice_draft: 'That billing is still a draft.',
  validation_error: 'A field has an invalid value — check quantities and amounts.',
};

function friendly(error: unknown, fallback: string): string {
  const code = errText(error);
  if (!code) return fallback;
  if (code.startsWith('out_of_stock')) {
    return 'The returned goods are no longer in stock at that branch, so the memo cannot be voided.';
  }
  return ERROR_COPY[code] ?? code;
}

const SELECT_CLS = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

const TAX_LABELS: Record<string, string> = {
  vatable: 'Vatable',
  exempt: 'VAT-exempt',
  zero_rated: 'Zero-rated',
};

/** Reasons offered in the picker; any other text is accepted. */
const REASONS = [
  'Returned goods',
  'Damaged goods',
  'Price adjustment',
  'Billing error',
  'Goodwill credit',
];

const STATUS_BADGE: Record<
  CreditMemoStatus,
  { label: string; variant: 'warning' | 'secondary' | 'success' | 'muted' }
> = {
  draft: { label: 'Draft', variant: 'warning' },
  open: { label: 'Open', variant: 'secondary' },
  applied: { label: 'Applied', variant: 'success' },
  refunded: { label: 'Refunded', variant: 'success' },
  void: { label: 'Void', variant: 'muted' },
};

type ProductLine = {
  key: string;
  kind: 'product';
  productId: string;
  variantId: string | null;
  label: string;
  qty: string;
  unitPrice: string;
  taxCode: string;
  incomeAccountCode: string;
};
type FreeTextLine = {
  key: string;
  kind: 'freetext';
  description: string;
  qty: string;
  unitPrice: string;
  taxCode: string;
  incomeAccountCode: string;
};
type EditorLine = ProductLine | FreeTextLine;

let keySeq = 0;
const nextKey = () => `cm${++keySeq}`;

function lineAmount(l: EditorLine): number {
  return (Number(l.qty) || 0) * (Number(sanitizeMoney(l.unitPrice)) || 0);
}

function toInput(l: EditorLine): ArInvoiceLineInput {
  const shared = {
    qty: Number(l.qty) || 0,
    unitPrice: sanitizeMoney(l.unitPrice),
    taxCode: l.taxCode || null,
    incomeAccountCode: l.incomeAccountCode || null,
  };
  return l.kind === 'product'
    ? { ...shared, productId: l.productId, variantId: l.variantId }
    : { ...shared, description: l.description };
}

export function CreditMemoEditorDialog({
  creditMemoId,
  onClose,
}: {
  /** null = a brand-new draft. */
  creditMemoId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const canWrite = useHasPerm(AR_PERMS.creditMemoWrite);
  const canVoid = useHasPerm(AR_PERMS.void);
  const canRefund = useHasPerm(AR_PERMS.creditRefundWrite);

  const detailQ = useQuery({
    queryKey: ['credit-memo', creditMemoId],
    queryFn: () => getCreditMemo(creditMemoId!),
    enabled: Boolean(creditMemoId),
  });
  const customersQ = useQuery({
    queryKey: ['customers', 'picker'],
    queryFn: () => listCustomers({ active: true }),
  });
  const branchesQ = useQuery({ queryKey: ['branches'], queryFn: () => listBranches() });
  const accountsQ = useQuery({ queryKey: ['gl-accounts'], queryFn: listGlAccounts });
  const incomeAccounts = (accountsQ.data?.rows ?? []).filter(
    (a) => a.active && a.type === 'income',
  );

  const [customerId, setCustomerId] = useState('');
  const [memoDate, setMemoDate] = useState(toDateInput());
  const [reason, setReason] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [reference, setReference] = useState('');
  const [memo, setMemo] = useState('');
  const [restock, setRestock] = useState(false);
  const [branchId, setBranchId] = useState('');
  const [lines, setLines] = useState<EditorLine[]>([]);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [applying, setApplying] = useState(false);
  const [refunding, setRefunding] = useState(false);

  // The customer's issued billings, for "raised against".
  const billingsQ = useQuery({
    queryKey: ['ar-invoices', 'for-credit-memo', customerId],
    queryFn: () => listArInvoices({ customerId }),
    enabled: Boolean(customerId),
  });
  const issuedBillings = (billingsQ.data ?? []).filter(
    (b) => b.status !== 'draft' && b.status !== 'void',
  );

  const cm = detailQ.data?.creditMemo;
  const isDraft = !creditMemoId || cm?.status === 'draft';
  const isVoid = cm?.status === 'void';
  const unapplied = Number(cm?.amountUnapplied ?? 0);

  // Seed the form from the loaded memo, once per memo.
  useEffect(() => {
    if (!detailQ.data) return;
    const { creditMemo: m, lines: rows } = detailQ.data;
    setCustomerId(m.customerId);
    setMemoDate(toDateInput(m.memoDate));
    setReason(m.reason);
    setInvoiceId(m.invoiceId ?? '');
    setReference(m.reference ?? '');
    setMemo(m.memo ?? '');
    setRestock(m.restock);
    setBranchId(m.branchId ?? '');
    setLines(
      rows.map((r) =>
        r.productId
          ? {
              key: nextKey(),
              kind: 'product',
              productId: r.productId,
              variantId: r.variantId,
              label: r.description || 'Product',
              qty: String(r.qty),
              unitPrice: trimMoney(r.unitPrice),
              taxCode: r.taxCode ?? '',
              incomeAccountCode: r.incomeAccountCode ?? '',
            }
          : {
              key: nextKey(),
              kind: 'freetext',
              description: r.description ?? '',
              qty: String(r.qty),
              unitPrice: trimMoney(r.unitPrice),
              taxCode: r.taxCode ?? '',
              incomeAccountCode: r.incomeAccountCode ?? '',
            },
      ),
    );
  }, [detailQ.data]);

  const total = useMemo(() => lines.reduce((s, l) => s + lineAmount(l), 0), [lines]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['credit-memos'] });
    void qc.invalidateQueries({ queryKey: ['credit-memo', creditMemoId] });
    void qc.invalidateQueries({ queryKey: ['ar-invoices'] });
    void qc.invalidateQueries({ queryKey: ['customer'] });
  };

  const body = () => ({
    invoiceId: invoiceId || null,
    memoDate: new Date(`${memoDate}T00:00:00`).toISOString(),
    reason: reason.trim(),
    reference: reference.trim() || null,
    memo: memo.trim() || null,
    restock,
    branchId: restock ? branchId || null : null,
    lines: lines.map(toInput),
  });

  const save = useMutation({
    mutationFn: async () =>
      creditMemoId
        ? updateCreditMemo(creditMemoId, body())
        : createCreditMemo({ customerId, ...body() }),
    onSuccess: () => {
      toast.success('Draft saved.');
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not save the draft.')),
  });

  const post = useMutation({
    mutationFn: () => postCreditMemo(creditMemoId!),
    onSuccess: (res) => {
      toast.success(
        Number(res.applied) > 0
          ? `Credit memo posted — ${peso(res.applied)} applied to the billing.`
          : 'Credit memo posted — the credit is on the customer’s account.',
      );
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not post this credit memo.')),
  });

  const del = useMutation({
    mutationFn: () => deleteCreditMemoDraft(creditMemoId!),
    onSuccess: () => {
      toast.success('Draft deleted.');
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not delete the draft.')),
  });

  const doVoid = useMutation({
    mutationFn: () => voidCreditMemo(creditMemoId!, voidReason.trim()),
    onSuccess: () => {
      toast.success('Credit memo voided.');
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(friendly(err, 'Could not void this credit memo.')),
  });

  const unapply = useMutation({
    mutationFn: (id: string) => unapplyArApplication(id, 'Unapplied from the credit memo'),
    onSuccess: () => {
      toast.success('Unapplied — the amount is back on the memo.');
      invalidate();
    },
    onError: (err) => toast.error(friendly(err, 'Could not unapply.')),
  });

  const voidRefund = useMutation({
    mutationFn: (id: string) => voidArCreditRefund(id, 'Voided from the credit memo'),
    onSuccess: () => {
      toast.success('Refund voided — the credit is back on the memo.');
      invalidate();
    },
    onError: (err) => toast.error(friendly(err, 'Could not void the refund.')),
  });

  const busy =
    save.isPending || post.isPending || del.isPending || doVoid.isPending || unapply.isPending;
  const canSave =
    canWrite && Boolean(customerId) && lines.length > 0 && reason.trim().length > 0 && !busy;

  type LinePatch = Partial<Omit<ProductLine, 'kind' | 'key'> & Omit<FreeTextLine, 'kind' | 'key'>>;
  const setLine = (key: string, patch: LinePatch) =>
    setLines((prev) => prev.map((l) => (l.key === key ? ({ ...l, ...patch } as EditorLine) : l)));

  const badge = cm ? STATUS_BADGE[cm.status] : null;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {cm ? `Credit memo ${cm.docNo}` : 'New credit memo'}
            {badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null}
          </DialogTitle>
        </DialogHeader>

        {detailQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Customer</label>
                <select
                  className={`${SELECT_CLS} w-full`}
                  value={customerId}
                  disabled={Boolean(creditMemoId)}
                  onChange={(e) => {
                    setCustomerId(e.target.value);
                    setInvoiceId('');
                  }}
                >
                  <option value="">Select a customer…</option>
                  {(customersQ.data?.rows ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Memo date</label>
                <Input
                  type="date"
                  className="h-9"
                  value={memoDate}
                  disabled={!isDraft}
                  onChange={(e) => setMemoDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground" htmlFor="cm-reason">
                  Reason
                </label>
                <Input
                  id="cm-reason"
                  className="h-9"
                  list="cm-reasons"
                  value={reason}
                  disabled={!isDraft}
                  placeholder="Returned goods, price adjustment…"
                  onChange={(e) => setReason(e.target.value)}
                />
                <datalist id="cm-reasons">
                  {REASONS.map((r) => (
                    <option key={r} value={r} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Against billing (optional)</label>
                <select
                  className={`${SELECT_CLS} w-full`}
                  value={invoiceId}
                  disabled={!isDraft || !customerId}
                  onChange={(e) => setInvoiceId(e.target.value)}
                >
                  <option value="">Not against a billing</option>
                  {issuedBillings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.docNo} — {peso(b.total)} ({b.status})
                    </option>
                  ))}
                </select>
                {isDraft ? (
                  <p className="text-xs text-muted-foreground">
                    Posting applies the credit to this billing as far as it still owes; the rest
                    stays as the customer&apos;s credit.
                  </p>
                ) : null}
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Reference</label>
                <Input
                  className="h-9"
                  value={reference}
                  disabled={!isDraft}
                  placeholder="Their return slip number, say"
                  onChange={(e) => setReference(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2 rounded-md border p-3">
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  className="mt-0.5"
                  checked={restock}
                  disabled={!isDraft}
                  onCheckedChange={(v) => setRestock(v === true)}
                />
                <span>
                  Put returned goods back in stock
                  <span className="block text-xs text-muted-foreground">
                    Only when the goods physically came back. Product lines are added to the
                    branch&apos;s stock when the memo posts, and their cost comes off cost of sales.
                  </span>
                </span>
              </label>
              {restock ? (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">Branch</label>
                  <select
                    className={`${SELECT_CLS} w-full`}
                    value={branchId}
                    disabled={!isDraft}
                    onChange={(e) => setBranchId(e.target.value)}
                  >
                    <option value="">Select the branch…</option>
                    {(branchesQ.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Lines</span>
                {isDraft && canWrite ? (
                  <div className="flex gap-2">
                    <div className="w-64">
                      <ProductPicker
                        selected={null}
                        placeholder="Add a product…"
                        onChange={(p) => {
                          if (!p) return;
                          setLines((prev) => [
                            ...prev,
                            {
                              key: nextKey(),
                              kind: 'product',
                              productId: p.id,
                              variantId: null,
                              label: p.name,
                              qty: '1',
                              unitPrice: '',
                              taxCode: '',
                              incomeAccountCode: '',
                            },
                          ]);
                        }}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setLines((prev) => [
                          ...prev,
                          {
                            key: nextKey(),
                            kind: 'freetext',
                            description: '',
                            qty: '1',
                            unitPrice: '',
                            taxCode: '',
                            incomeAccountCode: '',
                          },
                        ])
                      }
                    >
                      Add credit line
                    </Button>
                  </div>
                ) : null}
              </div>

              {lines.length === 0 ? (
                <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                  No lines yet. Add a returned product or a free-text credit.
                </p>
              ) : (
                <div className="space-y-2">
                  {lines.map((l) => (
                    <div key={l.key} className="grid gap-2 rounded-md border p-2 sm:grid-cols-12">
                      <div className="sm:col-span-4">
                        {l.kind === 'product' ? (
                          <div className="truncate pt-2 text-sm">{l.label}</div>
                        ) : (
                          <Input
                            className="h-9"
                            placeholder="Description"
                            value={l.description}
                            disabled={!isDraft}
                            onChange={(e) => setLine(l.key, { description: e.target.value })}
                          />
                        )}
                      </div>
                      <Input
                        className="h-9 sm:col-span-1"
                        type="number"
                        min={1}
                        value={l.qty}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { qty: e.target.value })}
                      />
                      <MoneyInput
                        className="h-9 sm:col-span-2"
                        value={l.unitPrice}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { unitPrice: e.target.value })}
                      />
                      <select
                        className={`${SELECT_CLS} sm:col-span-2`}
                        value={l.taxCode}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { taxCode: e.target.value })}
                      >
                        <option value="">Default tax</option>
                        {TAX_CODES.map((t) => (
                          <option key={t} value={t}>
                            {TAX_LABELS[t] ?? t}
                          </option>
                        ))}
                      </select>
                      <select
                        className={`${SELECT_CLS} sm:col-span-2`}
                        value={l.incomeAccountCode}
                        disabled={!isDraft}
                        onChange={(e) => setLine(l.key, { incomeAccountCode: e.target.value })}
                      >
                        <option value="">Sales Returns (4100)</option>
                        {incomeAccounts.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} — {a.name}
                          </option>
                        ))}
                      </select>
                      <div className="flex items-center justify-end gap-2 sm:col-span-1">
                        <span className="text-sm tabular-nums">{peso(lineAmount(l))}</span>
                        {isDraft && canWrite ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                          >
                            ×
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Memo</label>
                <Textarea
                  rows={3}
                  value={memo}
                  disabled={!isDraft}
                  onChange={(e) => setMemo(e.target.value)}
                />
              </div>
              <div className="space-y-2 rounded-md border p-3 text-sm">
                <div className="flex justify-between border-b pb-2 font-medium">
                  <span>Total credit</span>
                  <span className="tabular-nums">{peso(cm && !isDraft ? cm.total : total)}</span>
                </div>
                {cm && !isDraft ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Still available</span>
                      <span className="tabular-nums">
                        {isVoid ? '—' : peso(cm.amountUnapplied)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      VAT {peso(cm.vatAmount)}, frozen when the memo was posted.
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    VAT is computed from each line&apos;s tax code when you post.
                  </p>
                )}
              </div>
            </div>

            {cm && !isDraft && detailQ.data ? (
              <div className="space-y-3">
                <div>
                  <h3 className="mb-1 text-sm font-medium">Applied to billings</h3>
                  {detailQ.data.applications.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nothing applied yet.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody className="divide-y">
                        {detailQ.data.applications.map((a) => (
                          <tr key={a.id} className={a.status === 'void' ? 'opacity-60' : ''}>
                            <td className="py-1 font-mono text-xs">{a.invoiceDocNo}</td>
                            <td className="py-1 text-xs text-muted-foreground">
                              {shortDate(a.appliedAt)}
                            </td>
                            <td className="py-1 text-right tabular-nums">{peso(a.amount)}</td>
                            <td className="py-1 text-right">
                              {a.status === 'void' ? (
                                <Badge variant="muted">unapplied</Badge>
                              ) : canWrite ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={busy}
                                  onClick={() => unapply.mutate(a.id)}
                                >
                                  Unapply
                                </Button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-medium">Refunded</h3>
                  {detailQ.data.refunds.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No refunds.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody className="divide-y">
                        {detailQ.data.refunds.map((f) => (
                          <tr key={f.id} className={f.status === 'void' ? 'opacity-60' : ''}>
                            <td className="py-1 font-mono text-xs">{f.docNo}</td>
                            <td className="py-1 text-xs text-muted-foreground">
                              {shortDate(f.refundedAt)} · {f.method.replace(/_/g, ' ')}
                            </td>
                            <td className="py-1 text-right tabular-nums">{peso(f.amount)}</td>
                            <td className="py-1 text-right">
                              {f.status === 'void' ? (
                                <Badge variant="muted">void</Badge>
                              ) : canVoid ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={voidRefund.isPending}
                                  onClick={() => voidRefund.mutate(f.id)}
                                >
                                  Void
                                </Button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            ) : null}

            {voiding ? (
              <div className="space-y-2 rounded-md border border-destructive/40 p-3">
                <label className="text-xs text-muted-foreground">Reason for voiding</label>
                <Input
                  className="h-9"
                  value={voidReason}
                  autoFocus
                  onChange={(e) => setVoidReason(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setVoiding(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={voidReason.trim().length < 3 || busy}
                    onClick={() => doVoid.mutate()}
                  >
                    Void credit memo
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          {creditMemoId && !isDraft ? (
            <Button
              variant="outline"
              onClick={() =>
                window.open(`/accounting/ar/credit-memos/${creditMemoId}/print`, '_blank')
              }
            >
              Print
            </Button>
          ) : null}
          {cm && cm.status === 'open' && canWrite ? (
            <Button variant="outline" disabled={busy} onClick={() => setApplying(true)}>
              Apply to a billing
            </Button>
          ) : null}
          {cm && cm.status === 'open' && canRefund ? (
            <Button variant="outline" disabled={busy} onClick={() => setRefunding(true)}>
              Refund credit
            </Button>
          ) : null}
          <div className="flex-1" />
          {isDraft && creditMemoId && canWrite ? (
            <Button variant="ghost" disabled={busy} onClick={() => del.mutate()}>
              Delete draft
            </Button>
          ) : null}
          {!isDraft && !isVoid && canVoid ? (
            <Button variant="ghost" disabled={busy} onClick={() => setVoiding(true)}>
              Void
            </Button>
          ) : null}
          <Button variant="outline" disabled={busy} onClick={onClose}>
            Close
          </Button>
          {isDraft && canWrite ? (
            <Button disabled={!canSave} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save draft'}
            </Button>
          ) : null}
          {isDraft && creditMemoId && canWrite ? (
            <Button disabled={busy || lines.length === 0} onClick={() => post.mutate()}>
              {post.isPending ? 'Posting…' : 'Post'}
            </Button>
          ) : null}
        </DialogFooter>

        {applying && cm ? (
          <ApplyCreditDialog
            source={{ kind: 'credit_memo', id: cm.id }}
            customerId={cm.customerId}
            available={unapplied}
            onClose={() => setApplying(false)}
            onDone={() => {
              setApplying(false);
              invalidate();
            }}
          />
        ) : null}
        {refunding && cm ? (
          <RefundDialog
            source={{ kind: 'credit_memo', id: cm.id }}
            maxAmount={unapplied}
            onClose={() => setRefunding(false)}
            onDone={() => {
              setRefunding(false);
              invalidate();
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
